import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CancellationsApi from '../../../api/CancellationsApi';
import EndorsementsApi from '../../../api/EndorsementsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import DropdownButton from '../../DropdownButton';
import { successToast } from '../../Toast';
import ActionConfirmationModal from '../ActionConfirmationModal';
import ConfirmIssueModal from '../Cancellations/ConfirmIssueModal';

interface Props {
	cancellationData: CancellationData;
	authData: AuthData;
	errorData: ErrorData;
}

const CancellationActions = (props: Props) => {
	const navigate = useNavigate();
	const { authData, cancellationData, errorData } = props;
	const { hasPermission } = authData;
	const {
		selectedCancellation,
		cancellationUpdateObj,
		setCancellationUpdateObj,
		loadCancellation,
		setLoading,
		loadData,
		policy,
	} = cancellationData;
	const { state, policyLocator } = selectedCancellation;

	const { data: renewals } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		enabled: hasPermission('renewal.fetch'),
		staleTime: 1000 * 60 * 10, // 10 minutes
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const { data: endorsements } = useQuery({
		queryKey: [
			'endorsements',
			policy.locator,
			authData.auth.authorizationToken,
		],
		staleTime: 1000 * 60 * 10, // 10 minutes
		enabled: hasPermission('endorsement.fetch'),
		queryFn: () =>
			EndorsementsApi.getEndorsementsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const acceptedOrQuotedEndorsements =
		endorsements &&
		endorsements?.data?.some(
			(endorsement) =>
				endorsement.state === 'quoted' || endorsement.state === 'accepted',
		);
	const acceptedOrQuotedRenewals =
		renewals &&
		renewals?.data?.some(
			(renewal) => renewal.state === 'quoted' || renewal.state === 'accepted',
		);

	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const onIssue = async () => {
		setLoading(true);
		try {
			if (acceptedOrQuotedEndorsements || acceptedOrQuotedRenewals) {
				cancellationUpdateObj.conflictHandling = 'invalidate';
			}
			await CancellationsApi.updateCancellation(
				authData.auth.authorizationToken,
				selectedCancellation.locator,
				cancellationUpdateObj,
			);
			await CancellationsApi.issueCancellation(
				authData.auth.authorizationToken,
				selectedCancellation.locator,
			);
			successToast('Successfully issued cancellation');
			await loadData(policyLocator, selectedCancellation.locator);
			setCancellationUpdateObj({});
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to issue cancellation');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const onUpdate = async () => {
		if (acceptedOrQuotedEndorsements || acceptedOrQuotedRenewals) {
			cancellationUpdateObj.conflictHandling = 'invalidate';
		}

		setLoading(true);
		try {
			await CancellationsApi.updateCancellation(
				authData.auth.authorizationToken,
				selectedCancellation.locator,
				cancellationUpdateObj,
			);
			await loadCancellation(selectedCancellation.locator, policyLocator);
			setCancellationUpdateObj({});
			successToast('Successfully saved cancellation');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to update');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const hiddenOptions = [] as {
		label: string;
		callback: () => Promise<void>;
	}[];
	if (state === 'draft' && hasPermission('cancellation.rescind')) {
		const rescind = async () => {
			setLoading(true);
			try {
				await CancellationsApi.rescindCancellation(
					authData.auth.authorizationToken,
					selectedCancellation.locator,
				);
				await loadCancellation(selectedCancellation.locator, policyLocator);
			} catch (error) {
				errorData.handleErrorResponse(error, 'Failed to rescind cancellation');
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};
		hiddenOptions.push({ label: 'Rescind Cancellation', callback: rescind });
	}

	const isChanged = isEmpty(cancellationUpdateObj);

	const buttonList: any[] = [];
	if (state === 'draft' && hasPermission('cancellation.update')) {
		buttonList.push(
			<Button
				key={0}
				color='primary'
				variant='contained'
				disabled={isChanged}
				onClick={isChanged ? null : onUpdate}
			>
				Save
			</Button>,
		);
	}
	if (state === 'draft' && hasPermission('cancellation.issue')) {
		buttonList.push(
			<Button key={1} color='primary' variant='contained' onClick={onIssue}>
				Issue Cancellation
			</Button>,
		);
	}

	if (
		hasPermission('reinstatement.create') &&
		state === 'issued' &&
		(!selectedCancellation.reinstatement ||
			selectedCancellation.reinstatement?.state === 'expired')
	) {
		const reinstate = async () => {
			setLoading(true);
			try {
				const reinstatement = await CancellationsApi.reinstateCancellation(
					authData.auth.authorizationToken,
					selectedCancellation.locator,
				);
				successToast('Successfully reinstated cancellation');
				await loadCancellation(selectedCancellation.locator, policyLocator);

				if (reinstatement?.data?.locator) {
					navigate(`reinstatements/${reinstatement.data.locator}/details`);
				}
			} catch (error) {
				errorData.handleErrorResponse(
					error,
					'Failed to reinstate cancellation',
				);
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			}
			setLoading(false);
		};

		buttonList.push(
			<ActionConfirmationModal
				label='Reinstate Cancellation'
				message='Are you sure you want to reinstate this cancellation?'
				confirmText='Reinstate Cancellation'
				onConfirm={reinstate}
			/>,
		);
	}

	const Buttons = () => {
		return (
			<Stack direction='row' spacing={1}>
				{buttonList}
				{hiddenOptions.length ? (
					<DropdownButton actions={hiddenOptions} />
				) : null}
			</Stack>
		);
	};

	return (
		<>
			{hasPermission('cancellation.issue') && confirmModalOpen && (
				<ConfirmIssueModal
					onIssue={onIssue}
					cancel={() => setConfirmModalOpen(false)}
				/>
			)}
			<Buttons />
		</>
	);
};

export default withCancellationContext<Props>(CancellationActions);
